import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Material UI imports
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useMediaQuery, useTheme } from '@mui/system';

// Local imports
import Notification from '../Notification';
import Error from "../Error/Error";
import Loading from "../Loading";
import AudioRecorder from './AudioRecorder';
import { useSurveyResponse } from './SurveyResponseContext';
import { useActivitiesCompletion } from '../ActivitiesCompletionContext'
import { api } from '../../api';
import { ERROR_MESSAGES, ACTIVITY_TYPES } from '../../api/constants';


function a11yProps(index) {
  return {
    id: `response-tab-${index}`,
    'aria-controls': `response-tabpanel-${index}`,
  };
}


const SurveyQuestion = () => {
    const [alert, setAlert] = useState(false);
    const [alertData, setAlertData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [question, setQuestion] = useState(null);
    const [audioUrl, setAudioUrl] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [textResponse, setTextResponse] = useState('');
    const [audioResponse, setAudioResponse] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [noResponseAlert, setNoResponseAlert] = useState(false);

    const { responses, setResponses, updateResponses } = useSurveyResponse();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const idx = parseInt(searchParams.get('idx'), 10); // Get the question index from URL parameters
    const { t, i18n } = useTranslation();
    const currentLng = i18n.language;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { setReloadActivities } = useActivitiesCompletion();

    useEffect(() => {
    if (location.state && location.state.message) {
        setAlertData({severity: location.state.severity, message: location.state.message});
        setAlert(true);

        // Clear the state to prevent showing the alert again on refresh
        navigate(`${location.pathname}${location.search}`, { replace: true });
    }
    }, [location.state]);

    useEffect(() => {
        if (!idx || isNaN(idx) || idx < 1 || idx > 3) {
            setError('Invalid question index.');
            return;
        }

        // Fetch question data based on idx
        const fetchQuestion = async () => {
            try {
                const data = await api.get(`/survey/question?idx=${idx}`);
                setQuestion(data);
            } catch (err) {
                if (err.message === ERROR_MESSAGES.NOT_FOUND) {
                    setError(`Question with index ${idx} not found.`);
                } else setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        fetchQuestion();

        // Retrieve existing response for the current question index (if any)
        const existingResponse = responses.find(resp => resp.idx === idx);
        if (existingResponse) {
            if (existingResponse.textResponse !== undefined) {
                setTextResponse(existingResponse.textResponse);
            }
            if (existingResponse.audioResponse) {
                setAudioResponse(existingResponse.audioResponse);
            }
        } else {
            // If no existing response, initialize empty state
            setTextResponse('');
            setAudioResponse(null);
        }
    }, [idx])

    // Fetch signed audio URL for the question
    useEffect(() => {
        const fetchAudioUrl = async () => {
            const s3KeyAttr = `audios3Key_${currentLng}`;
            if (!question || !question[s3KeyAttr]) return;
            try {
                const data = await api.get(`/survey/${question.id}/audio?lang=${currentLng}`);
                setAudioUrl(data.audioUrl);
            } catch (err) {
                setError(`Failed to fetch audio: ${err.message}`);
            }
        }
        fetchAudioUrl();
    }, [question, currentLng]);

    const handleTabChange = (e, newVal) => {
        setTabValue(newVal);
    };

    // Get audio data from AudioRecorder component
    const handleAudioDataSend = (audioBlob) => {
        setAudioResponse(audioBlob);
    }

    const displayAlert = (severity, message) => {
        setAlertData({ severity, message });
        setNoResponseAlert(true);
    }

    const closeDialog = () => {
        setOpenDialog(false);
    }

    const handleLeave = () => {
        // Clear responses and navigate to survey topics page
        setResponses([]);
        navigate('/survey');
        closeDialog();
    }

    // Create a response object and save it to Survey Response context
    const saveResponse = () => {
        const respData = {};
        respData['questionId'] = question.id;
        respData['textResponse'] = textResponse;
        respData['audioResponse'] = audioResponse;
        respData['filename'] = audioResponse ? `response-${question.topicName}-${new Date()}.mp3` : null;
        updateResponses(idx, respData);
        setAudioResponse(null); // Clear local audio response after saving to context
    }

    const onPrevious = () => {
        // Clean notification
        setAlert(false);
        setNoResponseAlert(false);

        // If idx is 1, open a dialog to confirm leaving the survey
        if (idx === 1) {
            setOpenDialog(true);
            return;
        }

        // Save the response and navigate to the previous question
        saveResponse();
        navigate(`/survey/question?idx=${idx - 1}`,
            { state: { idx: idx } });
    }

    const onNext = () => {
        // Clean notification
        setAlert(false);
        setNoResponseAlert(false);

        // Display alert if no response is provided
        if (!textResponse && !audioResponse) {
            displayAlert('error', t('survey.alert_3'));
            return;
        }
        setNoResponseAlert(false);

        // Save response before navigating to the next question
        saveResponse();
        navigate(`/survey/question?idx=${idx + 1}`, 
            { state: { idx: idx } });
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        // Alert if no response is provided for the last question
        if (!textResponse && !audioResponse) {
            displayAlert('error', t('survey.alert_3'));
            return;
        }
        setNoResponseAlert(false);

        // Save the response 
        saveResponse();

        // Add the last response manually before submitting to make sure it's included
        const lastResponse = {
            idx: idx,
            questionId: question.id,
            textResponse: textResponse,
            audioResponse: audioResponse,
            filename: audioResponse && `response-${question.topicName}-${new Date()}.mp3`
        };

        const allResponses = [...responses, lastResponse];

        /* Submit all responses to the server */

        // Disable the submit button to prevent multiple clicks and change the text
        const submitBtn = document.querySelector('#submit');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = t('survey.submitting');
        }

        // Initialize audo upload key
        let s3Key = '';
        // Upload audio files to S3
        for (const resp of allResponses) {
            if (resp.audioResponse) {
                
                // Get signed URL for audio upload
                let uploadUrl;
                try {
                    const res = await api.post('/survey/audio/signed-url', { filename: resp.filename, contentType: 'audio/mp3' });
                    uploadUrl = res.uploadUrl;
                    s3Key = res.s3Key;
                } catch (err) {
                    console.error('Error getting upload URL:', err);
                    setError(t('survey.audio_upload_error'));
                    return;
                }

                // Upload audio file to S3
                try {
                    await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'audio/mp3'
                        },
                        body: resp.audioResponse
                    });
                } catch (err) {
                    console.error('Error uploading audio to S3:', err);
                    setError(t('survey.audio_upload_error'));
                    return;
                }
                delete resp.audioResponse; // Remove audio response from the object to avoid sending it in the request
                resp.audios3Key = s3Key; // Add S3 key to the response
            }

            // Save the response to the database
            try {
                await api.post('/survey/response/create', resp);
            } catch (err) {
                console.error('Error saving survey response:', err);
                setError(t('survey.submission_error'));
                return;
            }
        }

        // Log activity
        try {
            await api.post('/activity/create', { activityType: ACTIVITY_TYPES.USER_SUBMITTED_SURVEY });
        } catch (err) {
            console.error('Error logging activity:', err);
        }

        // Trigger reload of activities completion context
        setReloadActivities(true);

        // Clear responses after submission
        setResponses([]);

        // Navigate to Thank You page.
        navigate('/survey/submitted');
    }

    if (error) {
        return <Error message={error} />;
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <Fade in timeout={1000}>
            <div className="main">
                {/* Alert message */}
                {alert && (
                    <Notification
                        severity={alertData.severity} 
                        message={alertData.message}
                        onClose={() => {
                            setAlert(false);
                        }}
                        scroll={true}
                    />
                )}
                <Paper elevation={isMobile ? 0 : 3} sx={{ padding: { xs: 2, sm: 4 }, mb: 2, width: { xs: '100%', sm: '90%', ms: '70%' }, margin: 'auto', maxWidth: '700px' }}>
                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #ccc' }}>
                         {t('survey.question')} {idx}: {t(`survey.topic_${question.id}`)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', mb: 4 }}>
                        <Box component="img" src={question.imageUrl} alt={question.topicName} sx={{ flex: 1, height: '10rem', objectFit: 'cover', borderRadius: '10px' }} />
                        <Typography variant='body1' sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 2, sm: 0 }, flex: 2 }}>{t(`survey.question_${question.id}`)}</Typography>
                    </Box>
                    
                    { audioUrl ? (
                        <audio 
                            controls 
                            // autoPlay 
                            src={audioUrl} 
                            style={{ width: '40rem', maxWidth: '100%' }}
                        />
                    ) : (
                        <Typography variant='body2' color='textSecondary'>{t('survey.no_audio')}</Typography>
                    )}

                    {/* User's response */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ccc' }}>
                        <Box sx={{ mb: 2 }}>
                            <Tabs indicatorColor='' value={tabValue} onChange={handleTabChange} aria-label="Survey response tabs">
                                <Tab label={t('survey.text_response')} {...a11yProps(0)} />
                                <Tab label={t('survey.audio_response')} {...a11yProps(1)} />
                            </Tabs>
                        </Box>
                        <div 
                            role="tabpanel"
                            hidden={tabValue !== 0}
                            value={tabValue}
                            index={0}
                            id={'response-tabpanel-0'}
                            aria-labelledby={'response-tab-0'}
                        >
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder={t('survey.text_response_placeholder')}
                                sx={{ mb: 2, borderRadius: "20px" }}
                                name="textResponse"
                                id="textResponse"
                                value={textResponse}
                                onChange={(e) => setTextResponse(e.target.value)}
                            />
                        </div>
                        <div
                            role="tabpanel"
                            hidden={tabValue !== 1}
                            value={tabValue}
                            index={0}
                            id={'response-tabpanel-1'}
                            aria-labelledby={'response-tab-1'} 
                        >
                            <AudioRecorder onDataSend={handleAudioDataSend} idx={idx} />
                        </div>
                        {noResponseAlert && (
                            <Notification
                                severity={alertData.severity} 
                                message={alertData.message}
                                onClose={() => {
                                    setNoResponseAlert(false);
                                }}
                                scroll={true}
                            />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button variant='contained' onClick={onPrevious} sx={{ mr: 2, width: '8rem' }}>{t('survey.previous')}</Button>
                            {idx < 3 ? (
                                <Button variant="contained" onClick={onNext} sx={{ ml: 2, width: '8rem' }}>{t('survey.next')}</Button>
                            ) : (
                                <Button 
                                    variant="contained" 
                                    type="submit" 
                                    id="submit" 
                                    onClick={onSubmit} 
                                    sx={{ 
                                        ml: 2, 
                                        width: '8rem', 
                                        '&[disabled]': {
                                            pointerEvents: 'none',
                                            textDecoration: 'none',
                                            opacity: 0.5,
                                        } 
                                    }}>
                                    {t('survey.submit')}
                                </Button>
                            )}
                            
                        </Box>
                    </Box>
                </Paper>
                <Dialog
                    open={openDialog}
                    onClose={closeDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{t('survey.alert_4_title')}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">{t('survey.alert_4_description')}</DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ padding: 2 }}>
                        <Button onClick={closeDialog}>{t('survey.cancel')}</Button>
                        <Button onClick={handleLeave} autoFocus>{t('survey.leave')}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Fade>
    )
}

export default SurveyQuestion;